/// <reference types="office-js" />

import { Injectable } from '@angular/core';

/** Snapshot of mailbox + item fields used by the debug taskpane and API registration. */
export interface DebugOfficeMailSnapshot {
  internetMessageId: string;
  subject: string;
  fromEmail: string;
  fromDisplayName: string;
  dateTimeCreated: string;
  bodyPreview: string;
  userEmail: string;
  userDisplayName: string;
  ewsUrl: string;
  roamingSettingsSummary: string;
}

export interface DebugOfficeLoadResult {
  snapshot: DebugOfficeMailSnapshot;
  /** Non-fatal issues in section 1 (e.g. body read failure). */
  sectionWarnings: string[];
}

/**
 * Central wrapper for Outlook Office.js mailbox APIs.
 * All `Office.context` usage for this add-in must live in this service.
 */
@Injectable({
  providedIn: 'root',
})
export class MailService {
  /**
   * Resolves when the Office host has finished initialising for this add-in.
   */
  ensureOfficeReady(): Promise<void> {
    return Office.onReady().then(() => {
      if (Office.context == null) {
        throw new Error('Office-context is niet beschikbaar.');
      }
    });
  }

  /**
   * Registers a handler for {@link Office.EventType.ItemChanged} on the mailbox (e.g. pinned task pane).
   * Resolves with a function that removes the same handler.
   *
   * @remarks Requires Mailbox requirement set 1.5+.
   */
  registerMailboxItemChanged(onItemChanged: () => void): Promise<() => Promise<void>> {
    if (Office.context == null) {
      return Promise.reject(new Error('Office-context is niet beschikbaar.'));
    }
    const mailbox = Office.context.mailbox;
    const handler = (): void => {
      onItemChanged();
    };
    return new Promise((resolve, reject) => {
      mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }
        resolve(() =>
          new Promise<void>((unregisterResolve) => {
            if (Office.context == null) {
              unregisterResolve();
              return;
            }
            Office.context.mailbox.removeHandlerAsync(
              Office.EventType.ItemChanged,
              { handler } as Office.RemoveHandlerOptions,
              (removeResult) => {
                if (removeResult.status === Office.AsyncResultStatus.Failed) {
                  console.error('[MailService]', removeResult.error.message);
                }
                unregisterResolve();
              },
            );
          }),
        );
      });
    });
  }

  /**
   * SSO access token for Microsoft Graph / OBO (Identity API).
   */
  async getGraphSsoToken(): Promise<string> {
    if (Office.context == null) {
      throw new Error('Office-context is niet beschikbaar.');
    }
    return Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: true,
    });
  }

  /**
   * Collects mailbox, item, and roaming settings data for debug tooling.
   */
  async fetchDebugOfficeSnapshot(): Promise<DebugOfficeLoadResult> {
    if (Office.context == null) {
      throw new Error('Office-context is niet beschikbaar.');
    }

    const mailbox = Office.context.mailbox;
    const profile = mailbox.userProfile;
    const userEmail = profile?.emailAddress ?? '';
    const userDisplayName = profile?.displayName ?? '';
    const ewsUrl = mailbox.ewsUrl ?? '';

    const roamingSettingsSummary = Office.context.roamingSettings
      ? 'Beschikbaar (geen lijst van alle sleutels via Office.js API).'
      : 'Niet beschikbaar.';

    const item = mailbox.item;
    if (item == null) {
      return {
        snapshot: {
          internetMessageId: '',
          subject: '',
          fromEmail: '',
          fromDisplayName: '',
          dateTimeCreated: '',
          bodyPreview: '',
          userEmail,
          userDisplayName,
          ewsUrl,
          roamingSettingsSummary,
        },
        sectionWarnings: [],
      };
    }

    const message = item as Office.MessageRead;
    const sectionWarnings: string[] = [];
    let bodyPreview = '';
    try {
      bodyPreview = await this.readBodyTextPreview(message);
    } catch (err: unknown) {
      const messageText =
        err instanceof Error ? err.message : 'Tekst van de body kon niet worden gelezen.';
      sectionWarnings.push(messageText);
    }

    const created = message.dateTimeCreated;
    let dateTimeCreated = '';
    if (created instanceof Date) {
      dateTimeCreated = created.toISOString();
    } else if (typeof created === 'string') {
      dateTimeCreated = created;
    }

    const from = message.from;

    return {
      snapshot: {
        internetMessageId: message.internetMessageId ?? '',
        subject: message.subject ?? '',
        fromEmail: from?.emailAddress ?? '',
        fromDisplayName: from?.displayName ?? '',
        dateTimeCreated,
        bodyPreview,
        userEmail,
        userDisplayName,
        ewsUrl,
        roamingSettingsSummary,
      },
      sectionWarnings,
    };
  }

  /**
   * Returns the Internet message ID of the mail currently open in the host.
   *
   * @returns The `internetMessageId` from the active read item.
   * @throws {Error} `'Office not initialised'` when `Office.context` is null or undefined.
   * @throws {Error} When no read mail item is available or the ID is missing.
   */
  getCurrentMailId(): string {
    const item = this.getMessageReadItem();
    const id = item.internetMessageId;
    if (id == null || String(id).trim() === '') {
      throw new Error('Internet message ID is not available.');
    }
    return id;
  }

  /**
   * Returns the subject line of the mail currently open in the host.
   *
   * @returns The item subject, or an empty string when subject is absent.
   * @throws {Error} `'Office not initialised'` when `Office.context` is null or undefined.
   * @throws {Error} When no read mail item is available.
   */
  getSubject(): string {
    const item = this.getMessageReadItem();
    return item.subject ?? '';
  }

  /**
   * Opens a reply-all draft populated with the given HTML body. The user stays in control in Outlook.
   *
   * @param htmlBody HTML content for the reply body.
   * @throws {Error} `'Office not initialised'` when `Office.context` is null or undefined.
   * @throws {Error} When no read mail item is available or the reply form cannot be shown.
   */
  sendReply(htmlBody: string): void {
    this.getMessageReadItem().displayReplyAllForm({ htmlBody });
  }

  /**
   * Opens a reply draft (not reply-all) with the given HTML body.
   */
  openReplyFormFromHtml(htmlBody: string): void {
    this.getMessageReadItem().displayReplyForm({ htmlBody });
  }

  /**
   * Opens a reply-all draft with the given HTML body.
   */
  openReplyAllFormFromHtml(htmlBody: string): void {
    this.getMessageReadItem().displayReplyAllForm({ htmlBody });
  }

  /**
   * Opens a reply draft (not reply-all) with the given plain-text body converted to simple HTML.
   */
  openReplyFormFromPlainText(plainBody: string): void {
    this.openReplyFormFromHtml(this.plainBodyToReplyHtml(plainBody));
  }

  /**
   * Opens a reply-all draft with the given plain-text body converted to simple HTML.
   */
  openReplyAllFormFromPlainText(plainBody: string): void {
    this.openReplyAllFormFromHtml(this.plainBodyToReplyHtml(plainBody));
  }

  private plainBodyToReplyHtml(plain: string): string {
    const escaped = plain
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/\r\n|\n|\r/g, '<br/>');
    return `<div style="font-family:Segoe UI,sans-serif;font-size:11pt;">${escaped}</div>`;
  }

  private readBodyTextPreview(item: Office.MessageRead): Promise<string> {
    return new Promise((resolve, reject) => {
      item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }
        const text = String(result.value ?? '');
        resolve(text.slice(0, 300));
      });
    });
  }

  private getMessageReadItem(): Office.MessageRead {
    if (Office.context == null) {
      throw new Error('Office not initialised');
    }
    const mailItem = Office.context.mailbox.item;
    if (!mailItem) {
      throw new Error('No mail item is available.');
    }
    return mailItem as Office.MessageRead;
  }
}
